import React from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material'
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { Company, CompanyUser } from '../../../../core/models/Company'
import { StyledButton, StyledIconButton } from '../../styles/StyledComponents'
import { CompanyService } from '../../../../services/CompanyService'
import {
  billingService,
  BillingTier,
  BillingCalculation,
  CompanyBillingAccount,
} from '../../../../services/BillingService'
import { getRoleColor } from '../../types'
import { alpha, type Theme } from '@mui/material/styles'
import { CompanyBrandedShell } from '../CompanyBrandedShell'
import { getDisplayName } from '../../../../utils/personName'
import { useRbacStore } from '../../../../stores/rbacStore'
import { memberRoleLabel } from '../../utils/roleOptions'

interface CompanyUserViewProps {
  onBack?: () => void
  currentCompany: Company
}

function formatCompanyDate(value: Date | string | undefined): string | null {
  if (value === undefined || value === null) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString()
}

function memberDisplayName(u: CompanyUser): string {
  return getDisplayName({ ...u.profile, username: u.user?.username }, u.user?.email || 'Member')
}

function memberInitial(u: CompanyUser): string {
  const name = memberDisplayName(u)
  return name.charAt(0).toUpperCase() || '?'
}

/** Directory order: admins first, then managers, moderators, users, then unknown roles. */
function roleDirectoryOrder(role: string | undefined): number {
  const r = (role || '').toLowerCase()
  if (r === 'admin') return 0
  if (r === 'manager') return 1
  if (r === 'moderator') return 2
  if (r === 'user') return 3
  return 4
}

function compareMembersForDirectory(a: CompanyUser, b: CompanyUser): number {
  const ra = roleDirectoryOrder(a.role)
  const rb = roleDirectoryOrder(b.role)
  if (ra !== rb) return ra - rb
  return memberDisplayName(a).localeCompare(memberDisplayName(b), undefined, { sensitivity: 'base' })
}

export const CompanyUserView: React.FC<CompanyUserViewProps> = ({
  onBack,
  currentCompany,
}) => {
  const [companyData, setCompanyData] = React.useState(currentCompany)
  const [companyUsers, setCompanyUsers] = React.useState<CompanyUser[]>([])
  const [serverTotalUsers, setServerTotalUsers] = React.useState<number | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [refreshError, setRefreshError] = React.useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [memberSearch, setMemberSearch] = React.useState('')

  const [billingTiers, setBillingTiers] = React.useState<BillingTier[]>([])
  const [billingAccount, setBillingAccount] = React.useState<CompanyBillingAccount | null>(null)
  const [billingCalculation, setBillingCalculation] = React.useState<BillingCalculation | null>(null)
  const [billingDataLoaded, setBillingDataLoaded] = React.useState(false)
  const [billingLoading, setBillingLoading] = React.useState(false)

  /**
   * "Already fetched / in flight" guard, held in a ref rather than read out of state.
   *
   * loadBillingData used to list billingDataLoaded and billingLoading in its own
   * dependency array while also setting both, so every one of those writes produced a new
   * callback identity, which re-ran the effect below, which called loadCompanyUsers again,
   * which flipped `loading` and blanked the whole grid. Four renders of load-blank-show
   * for one page visit. A ref gives the same guard without feeding the dependency array.
   * [SC-313]
   */
  const billingRequestRef = React.useRef<{ companyId: string | null; loaded: boolean; inFlight: boolean }>(
    { companyId: null, loaded: false, inFlight: false },
  )

  /**
   * The company each load was started for. Resetting the ref on a company change did nothing
   * about the request already in flight: company A's response could land after the switch to
   * B, write A's billing account and calculation into B's view, and then set `loaded = true`
   * on what is now *B's* ref — so B never fetched its own data, and an error from B left A's
   * numbers on screen with retries suppressed. Same race on the member list. Every write past
   * an await is now gated on the company still being the current one. [SC-313]
   */
  const requestedCompanyRef = React.useRef(currentCompany.id)
  requestedCompanyRef.current = currentCompany.id
  const isCurrentCompany = React.useCallback(
    (companyId: string) => requestedCompanyRef.current === companyId,
    [],
  )

  const loadCompanyUsers = React.useCallback(async () => {
    const companyId = currentCompany.id
    try {
      setLoading(true)
      setError(null)
      const result = await CompanyService.getCompanyUsersAll(companyId)
      if (!isCurrentCompany(companyId)) return
      if (result.success && result.users) {
        setCompanyUsers(result.users)
        if (result.total !== undefined) {
          setServerTotalUsers(result.total)
        }
      }
    } catch (err) {
      if (!isCurrentCompany(companyId)) return
      console.error('Error loading company users:', err)
      setError('Failed to load company information')
    } finally {
      // A superseded load must not clear the current one's spinner.
      if (isCurrentCompany(companyId)) setLoading(false)
    }
  }, [currentCompany.id, isCurrentCompany])

  const loadBillingData = React.useCallback(async () => {
    const companyId = currentCompany.id
    // A different company means the previous answer no longer applies.
    if (billingRequestRef.current.companyId !== companyId) {
      billingRequestRef.current = { companyId, loaded: false, inFlight: false }
    }
    if (billingRequestRef.current.loaded || billingRequestRef.current.inFlight) return
    billingRequestRef.current.inFlight = true

    try {
      setBillingLoading(true)

      const tiersResult = await billingService.getBillingTiers(true)
      if (!isCurrentCompany(companyId)) return
      if (tiersResult.success) {
        setBillingTiers(tiersResult.billing_tiers)
      }

      const billingResult = await billingService.getComprehensiveBillingData(companyId, {
        include_history: false,
        include_upcoming: false,
      })
      if (!isCurrentCompany(companyId)) return

      if (billingResult.success) {
        if (billingResult.billing_account) {
          setBillingAccount(billingResult.billing_account)
        }
        if (billingResult.billing_calculation) {
          setBillingCalculation(billingResult.billing_calculation)
        }
      }

      billingRequestRef.current.loaded = true
      setBillingDataLoaded(true)
    } catch (err) {
      console.error('Error loading billing data:', err)
    } finally {
      // Only release the guard the *current* company owns. A stale load returning early
      // above must not mark this company loaded, nor clear its in-flight flag — that is what
      // let a superseded response suppress the new company's own fetch.
      if (billingRequestRef.current.companyId === companyId) {
        billingRequestRef.current.inFlight = false
      }
      if (isCurrentCompany(companyId)) setBillingLoading(false)
    }
  }, [currentCompany.id, isCurrentCompany])

  React.useEffect(() => {
    loadCompanyUsers()
    loadBillingData()
  }, [loadCompanyUsers, loadBillingData])

  React.useEffect(() => {
    setCompanyData(currentCompany)
  }, [currentCompany])

  const refreshCompanyData = React.useCallback(async () => {
    if (isRefreshing) return

    try {
      setIsRefreshing(true)
      setRefreshError(null)

      const [companyResult, usersResult, billingTiersResult] = await Promise.all([
        CompanyService.getCompany(currentCompany.id),
        CompanyService.getCompanyUsersAll(currentCompany.id),
        billingService.getBillingTiers(true),
      ])

      if (companyResult.success && companyResult.company) {
        setCompanyData(companyResult.company)
      } else if (!companyResult.success) {
        throw new Error(companyResult.error || 'Failed to refresh company data')
      }

      if (usersResult.success && usersResult.users) {
        setCompanyUsers(usersResult.users)
        if (usersResult.total !== undefined) {
          setServerTotalUsers(usersResult.total)
        }
      } else if (!usersResult.success) {
        throw new Error(usersResult.error || 'Failed to refresh company users')
      }

      if (billingTiersResult.success) {
        setBillingTiers(billingTiersResult.billing_tiers)
      } else {
        console.warn('Failed to refresh billing tiers')
      }

      const billingResult = await billingService.getComprehensiveBillingData(currentCompany.id, {
        include_history: false,
        include_upcoming: false,
      })

      if (billingResult.success) {
        if (billingResult.billing_account) {
          setBillingAccount(billingResult.billing_account)
        }
        if (billingResult.billing_calculation) {
          setBillingCalculation(billingResult.billing_calculation)
        }
        setBillingDataLoaded(true)
      } else {
        console.warn('Failed to refresh billing account:', billingResult.error)
      }
    } catch (err) {
      console.error('Error refreshing company data:', err)
      const errorMessage = err instanceof Error ? err.message : 'Unable to refresh data. Please try again.'
      setRefreshError(errorMessage)
    } finally {
      setIsRefreshing(false)
    }
  }, [currentCompany.id])

  const getCurrentTierMaxUsers = () => {
    if (billingAccount?.billing_tier?.max_users !== undefined) {
      return billingAccount.billing_tier.max_users === -1 ? 'Unlimited' : billingAccount.billing_tier.max_users
    }

    const currentTier = billingTiers.find(tier => tier.tier_key === companyData.billing_tier)
    if (currentTier) {
      return currentTier.max_users === -1 ? 'Unlimited' : currentTier.max_users
    }

    return companyData.max_users || 100
  }

  const fromRoster = serverTotalUsers ?? companyUsers.length
  const fromBilling =
    billingDataLoaded && billingCalculation != null && typeof billingCalculation.active_users === 'number'
      ? billingCalculation.active_users
      : null
  const activeMemberCount = fromBilling != null ? Math.max(fromRoster, fromBilling) : fromRoster

  const planSeatsDisplay = getCurrentTierMaxUsers()
  const planSeatsNum = planSeatsDisplay === 'Unlimited' ? Infinity : Number(planSeatsDisplay)
  const overCapacity =
    !loading && !billingLoading && planSeatsNum !== Infinity && activeMemberCount > planSeatsNum
  const capacityPct =
    planSeatsNum === Infinity || billingLoading ? null : Math.round((activeMemberCount / planSeatsNum) * 100)

  // Names the screen already holds, for rows that carry a key but no name; never fetched here.
  const knownRoles = useRbacStore((s) => s.catalogue?.systemRoles)

  const filteredMembers = React.useMemo(() => {
    const q = memberSearch.trim().toLowerCase()
    const list = !q
      ? [...companyUsers]
      : companyUsers.filter(u => {
          const name = memberDisplayName(u).toLowerCase()
          const email = (u.user?.email || '').toLowerCase()
          const role = memberRoleLabel(u, u.role || '', knownRoles).toLowerCase()
          return name.includes(q) || email.includes(q) || role.includes(q)
        })
    list.sort(compareMembersForDirectory)
    return list
  }, [companyUsers, memberSearch, knownRoles])

  // Themed rather than hardcoded. This view was written with a light palette baked in
  // (#ffffff surfaces, #424242 text), so it rendered as a light page inside an otherwise
  // dark app. CompanyManagerView — the same page for managers — has always used theme
  // tokens, which is why only the plain-user view looked wrong. [SC-313]
  const cardStyle = (t: Theme) => ({
    p: 2.5,
    height: '100%',
    minHeight: '260px',
    border: `1px solid ${t.palette.divider}`,
    borderRadius: '12px',
    backgroundColor: t.palette.background.paper,
    boxShadow: t.shadows[1],
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: t.palette.primary.main,
      boxShadow: t.shadows[3],
    },
  })

  const metricTileShell = (over: boolean) => (t: Theme) => ({
    borderRadius: 1.5,
    backgroundColor: over ? alpha(t.palette.error.main, 0.08) : t.palette.action.hover,
    border: `1px solid ${over ? alpha(t.palette.error.main, 0.25) : t.palette.divider}`,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.25,
    py: 1.5,
    px: 1,
    textAlign: 'center' as const,
  })

  const trialEnd = formatCompanyDate(companyData.trial_ends_at)
  const subscriptionEnd = formatCompanyDate(companyData.subscription_ends_at)

  return (
    <CompanyBrandedShell company={companyData}>
    <Box sx={{ width: '100%' }}>
      <Box
        sx={(t) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2.5,
          pb: 2,
          borderBottom: `1px solid ${t.palette.divider}`,
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onBack && (
            <StyledIconButton
              onClick={onBack}
              sx={(t) => ({
                border: `1px solid ${t.palette.divider}`,
                borderRadius: '8px',
                p: 0.75,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: alpha(t.palette.primary.main, 0.08),
                },
              })}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </StyledIconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {companyData.logo_url ? (
              <Avatar
                src={companyData.logo_url}
                alt=""
                variant="rounded"
                sx={(t) => ({ width: 48, height: 48, border: `1px solid ${t.palette.divider}` })}
              />
            ) : (
              <Box
                sx={(t) => ({
                  p: 1,
                  borderRadius: '8px',
                  background: alpha(t.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
                })}
              >
                <BusinessIcon sx={{ fontSize: 22, color: 'primary.main' }} />
              </Box>
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.5rem' }}>
                My Company
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                {companyData.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <StyledButton
          variant="outlined"
          onClick={refreshCompanyData}
          size="small"
          disabled={isRefreshing}
          startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
        >
          {isRefreshing ? 'Refreshing…' : 'Refresh'}
        </StyledButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {refreshError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefreshError(null)}>
          {refreshError}
        </Alert>
      )}

      {overCapacity && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Plan capacity exceeded
          </Typography>
          <Typography variant="body2">
            This organization has more active members than the included seats on the current plan. Billing may include
            additional charges. <strong>New members may be restricted</strong> until a company manager upgrades the plan.
            If you need access for more people, contact your company administrator.
          </Typography>
        </Alert>
      )}

      <Box
        sx={(t) => ({
          borderRadius: 2.5,
          overflow: 'hidden',
          border: `1px solid ${t.palette.divider}`,
          backgroundColor: t.palette.background.paper,
          boxShadow: t.shadows[2],
        })}
      >
        <Box
          sx={(t) => ({
            background: `linear-gradient(135deg, ${t.palette.background.default} 0%, var(--sevv-surface-tint) 100%)`,
            minHeight: '100%',
            p: 2,
            borderRadius: 2,
          })}
        >
          {/* `loading` is only set on mount and on a company change — refreshing uses
              isRefreshing — so a spinner here is right both times. Switching company must
              not leave the previous company's members on screen under a new name. */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
              <CircularProgress sx={{ color: 'primary.main' }} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={cardStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={(t) => ({
                        p: 0.75,
                        borderRadius: 1.5,
                        backgroundColor: 'var(--sevv-surface-tint)',
                        border: `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
                        mr: 1.5,
                      })}
                    >
                      <BusinessIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1rem' }}>
                      Company Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <InfoRow label="Name" value={companyData.name} />
                    {companyData.domain && <InfoRow label="Domain" value={companyData.domain} />}
                    {companyData.subdomain && <InfoRow label="Subdomain" value={companyData.subdomain} />}
                    {companyData.platform_name && <InfoRow label="Platform" value={companyData.platform_name} />}
                    {companyData.platform_url && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '100px' }}>
                          Website
                        </Typography>
                        <Link href={companyData.platform_url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>
                          {companyData.platform_url}
                        </Link>
                      </Box>
                    )}
                    {companyData.auto_assignment_enabled !== undefined && (
                      <InfoRow
                        label="Sync existing accounts"
                        value={companyData.auto_assignment_enabled ? 'On' : 'Off'}
                      />
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '100px' }}>
                        Status
                      </Typography>
                      <Chip
                        label={companyData.status}
                        size="small"
                        sx={(t) => ({
                          bgcolor:
                            companyData.status === 'active'
                              ? alpha(t.palette.success.main, 0.12)
                              : t.palette.action.selected,
                          color: companyData.status === 'active' ? 'success.main' : 'text.secondary',
                          fontWeight: 500,
                          height: 24,
                          fontSize: '0.75rem',
                        })}
                      />
                    </Box>
                    {trialEnd && <InfoRow label="Trial ends" value={trialEnd} />}
                    {subscriptionEnd && <InfoRow label="Subscription ends" value={subscriptionEnd} />}
                    {companyData.billing_tier && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '100px' }}>
                          Plan
                        </Typography>
                        <Chip
                          label={companyData.billing_tier}
                          size="small"
                          sx={(t) => ({
                            bgcolor: alpha(t.palette.info.main, 0.12),
                            color: 'info.main',
                            fontWeight: 500,
                            height: 24,
                            fontSize: '0.75rem',
                          })}
                        />
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={(t) => ({
                    ...cardStyle(t),
                    height: 'auto',
                    minHeight: 'auto',
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={(t) => ({
                        p: 0.75,
                        borderRadius: 1.5,
                        backgroundColor: 'var(--sevv-surface-tint)',
                        border: `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
                        mr: 1.5,
                      })}
                    >
                      <PeopleIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1rem' }}>
                      Membership
                    </Typography>
                  </Box>
                  {billingLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1.25,
                        alignItems: 'stretch',
                        width: '100%',
                      }}
                    >
                      <Box sx={metricTileShell(overCapacity)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          {overCapacity && <WarningIcon sx={{ fontSize: 20, color: 'error.main', flexShrink: 0 }} />}
                          <Typography
                            component="div"
                            variant="h6"
                            sx={{
                              color: overCapacity ? 'error.main' : 'primary.main',
                              fontWeight: 700,
                              lineHeight: 1.2,
                              fontSize: '1.25rem',
                            }}
                          >
                            {activeMemberCount}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.02 }}>
                          Members
                        </Typography>
                        <Box sx={{ height: 14, lineHeight: '14px', fontSize: '0.7rem', visibility: 'hidden' }} aria-hidden>
                          &nbsp;
                        </Box>
                      </Box>
                      <Box sx={metricTileShell(overCapacity)}>
                        <Typography
                          component="div"
                          variant="h6"
                          sx={{
                            color: overCapacity ? 'error.main' : 'info.main',
                            fontWeight: 700,
                            lineHeight: 1.25,
                            fontSize: planSeatsDisplay === 'Unlimited' ? '0.95rem' : '1.25rem',
                            textAlign: 'center',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                          }}
                        >
                          {planSeatsDisplay}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.02 }}>
                          Plan seats
                        </Typography>
                        <Box
                          sx={{
                            height: 14,
                            lineHeight: '14px',
                            fontSize: '0.7rem',
                            color: 'error.main',
                            visibility: overCapacity ? 'visible' : 'hidden',
                          }}
                          aria-hidden={!overCapacity}
                        >
                          {overCapacity ? 'Limit exceeded' : '\u00a0'}
                        </Box>
                      </Box>
                      <Box sx={metricTileShell(overCapacity)}>
                        <Typography
                          component="div"
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.2,
                            fontSize: '1.25rem',
                            color:
                              overCapacity
                                ? 'error.main'
                                : capacityPct !== null && capacityPct >= 80
                                  ? 'warning.main'
                                  : 'secondary.main',
                          }}
                        >
                          {capacityPct === null ? '—' : `${capacityPct}%`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.02 }}>
                          Capacity used
                        </Typography>
                        <Box sx={{ height: 14, lineHeight: '14px', fontSize: '0.7rem', visibility: 'hidden' }} aria-hidden>
                          &nbsp;
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={(t) => ({
                    ...cardStyle(t),
                    minHeight: 0,
                    p: 0,
                    overflow: 'hidden',
                  })}
                >
                  <Box
                    sx={(t) => ({
                      px: 2.5,
                      py: 2,
                      borderBottom: `1px solid ${t.palette.divider}`,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PeopleIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Team directory
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {activeMemberCount} member{activeMemberCount !== 1 ? 's' : ''}
                          {filteredMembers.length !== companyUsers.length &&
                            ` · ${filteredMembers.length} shown`}
                        </Typography>
                      </Box>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Search by name, email, or role"
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 260, maxWidth: '100%' }}
                    />
                  </Box>
                  <List
                    dense
                    sx={{
                      maxHeight: 380,
                      overflow: 'auto',
                      py: 0,
                    }}
                  >
                    {filteredMembers.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary="No members match your search"
                          secondary={memberSearch ? 'Try a different keyword' : 'No members loaded'}
                          sx={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                    ) : (
                      filteredMembers.map((u, index) => (
                        <React.Fragment key={u.id || u.user_id}>
                          {index > 0 && <Divider component="li" />}
                          <ListItem alignItems="flex-start" sx={{ py: 1.25, px: 2.5 }}>
                            <ListItemAvatar>
                              <Avatar
                                src={u.profile?.avatar || undefined}
                                sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '1rem' }}
                              >
                                {memberInitial(u)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography component="span" variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {memberDisplayName(u)}
                                  </Typography>
                                  <Chip
                                    label={memberRoleLabel(u, u.role, knownRoles)}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.7rem',
                                      bgcolor: `${getRoleColor(u.role)}15`,
                                      color: getRoleColor(u.role),
                                      fontWeight: 600,
                                    }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                                  {u.user?.email || '—'}
                                </Typography>
                              }
                            />
                          </ListItem>
                        </React.Fragment>
                      ))
                    )}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
    </CompanyBrandedShell>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px', gap: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '100px' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, textAlign: 'right', flex: 1 }}>
        {value}
      </Typography>
    </Box>
  )
}
